import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { CheckIcon, ChevronRightIcon, ChevronsUpDownIcon } from "lucide-react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { toast } from "sonner";
import type { ColumnDef, ExpandedState } from "@tanstack/react-table";
import type {
  Incident,
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
} from "@/lib/queries/incidents";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  INCIDENT_TYPE,
  incidentSeveritySchema,
  incidentStatusSchema,
  incidentTypeSchema,
  incidentsListQueryOptions,
  useIncidentsList,
} from "@/lib/queries/incidents";
import { usersListQueryOptions } from "@/lib/queries/users";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";

import { api } from "@/lib/http";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/ui/status-badge";
import { Spinner } from "@/components/ui/spinner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_LABELS: Record<IncidentStatus, string> = {
  OPEN: "Open",
  ASSIGNED: "Assigned",
  IN_REVIEW: "In Review",
  RESOLVED: "Resolved",
  FALSE_POSITIVE: "False Positive",
};

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
  status: incidentStatusSchema.optional(),
  severity: incidentSeveritySchema.optional(),
  type: incidentTypeSchema.optional(),
  order_by: z.string().catch("-created_at"),
});

export const Route = createFileRoute("/_app/incidents/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(incidentsListQueryOptions(deps));
  },
});

function severityVariant(severity: IncidentSeverity) {
  if (severity === "Critical") return "error" as const;
  if (severity === "High") return "warning-error" as const;
  if (severity === "Medium") return "warning" as const;
  return "success" as const;
}

function statusVariant(status: IncidentStatus) {
  if (status === "OPEN") return "default" as const;
  if (status === "ASSIGNED") return "info" as const;
  if (status === "IN_REVIEW") return "warning" as const;
  if (status === "RESOLVED") return "success" as const;
  return "error" as const;
}

function actionVariant(action: string) {
  const normalized = action.toLowerCase().replace(/[_-]+/g, " ");
  if (
    normalized === "blocked" ||
    normalized === "prevented" ||
    normalized === "redacted"
  ) {
    return normalized === "redacted"
      ? ("warning-error" as const)
      : ("error" as const);
  }
  if (normalized === "monitored" || normalized === "no action") {
    return "info" as const;
  }
  return "default" as const;
}

function formatAction(action: string) {
  return action
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getContextSubtitle(context: Incident["context"]) {
  if (!context) return null;
  if (context.kind === "gateway") {
    const parts = [context.gateway_key_name, context.model].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : null;
  }
  if (context.kind === "desktop") {
    const appName = context.app_name?.trim() || null;
    const website = context.website?.trim() || null;
    if (appName && website) {
      const alreadyIncludesWebsite = appName
        .toLowerCase()
        .includes(website.toLowerCase());
      return alreadyIncludesWebsite ? appName : `${appName} · ${website}`;
    }
    return appName ?? website;
  }
  if (context.kind === "chat") return context.chat_id ?? null;
  return context.label ?? null;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const columns = [
  {
    header: "Detected",
    id: "detected",
    accessorFn: (row) => row.subject?.label ?? "—",
    enableSorting: false,
    size: 200,
    cell: ({ row, getValue }) => (
      <div className="flex items-center gap-2">
        <ChevronRightIcon
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            row.getIsExpanded() && "rotate-90",
          )}
        />
        <span className="font-medium">{getValue<string>()}</span>
      </div>
    ),
  },
  {
    header: "Source",
    accessorKey: "type",
    enableSorting: false,
    size: 240,
    cell: ({ row }) => {
      const subtitle = getContextSubtitle(row.original.context);
      return (
        <div className="flex flex-col items-start gap-1">
          <StatusBadge variant="default">{row.original.type}</StatusBadge>
          {subtitle ? (
            <span className="text-xs text-muted-foreground">{subtitle}</span>
          ) : null}
        </div>
      );
    },
  },
  {
    header: "Severity",
    accessorKey: "severity",
    enableSorting: false,
    size: 110,
    cell: ({ getValue }) => {
      const severity = getValue<IncidentSeverity>();
      return (
        <StatusBadge variant={severityVariant(severity)}>
          {severity}
        </StatusBadge>
      );
    },
  },
  {
    header: "Status",
    accessorKey: "status",
    enableSorting: false,
    size: 130,
    cell: ({ getValue }) => {
      const status = getValue<IncidentStatus>();
      return (
        <StatusBadge variant={statusVariant(status)}>
          {STATUS_LABELS[status]}
        </StatusBadge>
      );
    },
  },
  {
    header: "Action taken",
    id: "action",
    accessorFn: (row) => row.subject?.action ?? null,
    enableSorting: false,
    size: 130,
    cell: ({ getValue }) => {
      const action = getValue<string | null | undefined>();
      if (!action) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <StatusBadge variant={actionVariant(action)}>
          {formatAction(action)}
        </StatusBadge>
      );
    },
  },
  {
    header: "User",
    accessorKey: "triggered_by_user.full_name",
    size: 160,
    enableSorting: false,
    cell: ({ getValue }) => {
      const user = getValue<string | undefined>();
      return user ? (
        <span>{user}</span>
      ) : (
        <span className="text-muted-foreground">N/A</span>
      );
    },
  },
  {
    header: "Created",
    accessorKey: "created_at",
    size: 140,
    cell: ({ getValue }) => formatDate(getValue<string>()),
  },
] as const satisfies Array<ColumnDef<Incident>>;

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const incidents = useIncidentsList(searchParams);

  const isDescending = searchParams.order_by.startsWith("-");
  const sorting = [
    {
      desc: isDescending,
      id: isDescending ? searchParams.order_by.slice(1) : searchParams.order_by,
    },
  ];

  const table = useReactTable({
    data: incidents.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    getRowId: (row) => row.id,
    getRowCanExpand: () => true,
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      sorting,
      expanded,
    },
    onExpandedChange: setExpanded,
    onSortingChange(s) {
      const newSorting = typeof s === "function" ? s(sorting) : s;
      const lastSort = newSorting.at(-1);

      navigate({
        from: Route.fullPath,
        search: (old) => ({
          ...old,
          page: 1,
          order_by: lastSort
            ? `${lastSort.desc ? "-" : ""}${lastSort.id}`
            : "-created_at",
        }),
      });
    },
    rowCount: incidents.data?.count ?? 1,
    manualPagination: true,
    manualFiltering: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold">Incidents</h1>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
          <Select
            value={searchParams.status || "ALL"}
            onValueChange={(value: IncidentStatus | "ALL") =>
              navigate({
                search: (old) => ({
                  ...old,
                  status: value === "ALL" ? undefined : value,
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[130px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              {INCIDENT_STATUS.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={searchParams.severity || "ALL"}
            onValueChange={(value: IncidentSeverity | "ALL") =>
              navigate({
                search: (old) => ({
                  ...old,
                  severity: value === "ALL" ? undefined : value,
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[140px]">
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Severities</SelectItem>
              {INCIDENT_SEVERITY.map((sev) => (
                <SelectItem key={sev} value={sev}>
                  {sev}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={searchParams.type || "ALL"}
            onValueChange={(value: IncidentType | "ALL") =>
              navigate({
                search: (old) => ({
                  ...old,
                  type: value === "ALL" ? undefined : value,
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[120px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              {INCIDENT_TYPE.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            className="border-primary text-primary hover:bg-primary/5 hover:text-primary"
            onClick={() =>
              navigate({
                search: (old) => ({
                  ...old,
                  status: undefined,
                  severity: undefined,
                  type: undefined,
                  page: 1,
                }),
              })
            }
          >
            Reset Filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <GenericTable
          sorting
          table={table}
          isLoading={incidents.isLoading}
          renderExpandedRow={(row) => (
            <IncidentExpandedContent incident={row.original} />
          )}
        />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

function IncidentExpandedContent({ incident }: { incident: Incident }) {
  const entity = incident.message_entity;

  return (
    <div className="space-y-5">
      {entity ? (
        <section>
          <h3 className="mb-2 text-sm text-muted-foreground">Detected PII</h3>
          <div className="space-y-2.5 rounded-lg border bg-muted/20 px-4 py-3">
            <DetailRow label="Original" value={entity.entity_text} />
            <div className="flex items-start gap-3 text-sm">
              <span className="w-24 shrink-0 font-medium">Masked:</span>
              {entity.masked_entity_text ? (
                <span className="rounded-md border bg-muted px-1.5 py-0.5 font-mono text-xs">
                  {entity.masked_entity_text}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
            <DetailRow
              label="Confidence"
              value={entity.confidence_score.toFixed(2)}
            />
          </div>
        </section>
      ) : null}

      <div className="flex justify-end gap-2">
        <AssignDialog incident={incident} />
        <ResolveDialog incident={incident} />
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="w-24 shrink-0 font-medium">{label}:</span>
      <span className="font-mono">{value || "—"}</span>
    </div>
  );
}

function AssignDialog({ incident }: { incident: Incident }) {
  const [open, setOpen] = React.useState(false);
  const [userId, setUserId] = React.useState<string | null>(
    incident.assigned_to_user?.id ?? null,
  );
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (assignedUserId: string) =>
      api.raw.patch(`api/v1/incidents/${incident.id}/assign`, {
        json: { user_to_assign_id: assignedUserId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents", "list"] });
      setOpen(false);
      toast.success("Incident assigned successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to assign incident");
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setUserId(incident.assigned_to_user?.id ?? null);
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline">
          Assign
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Incident</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Assign this incident to a team member for investigation.
          </p>
          <AssignUserPicker value={userId} onValueChange={setUserId} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!userId || mutation.isPending}
              onClick={() => {
                if (userId) mutation.mutate(userId);
              }}
            >
              {mutation.isPending ? "Assigning..." : "Assign"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResolveDialog({ incident }: { incident: Incident }) {
  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState<IncidentStatus>("RESOLVED");
  const [reason, setReason] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: {
      status: IncidentStatus;
      reason?: string;
      notes?: string;
    }) =>
      api.raw.patch(`api/v1/incidents/${incident.id}/status`, {
        json: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents", "list"] });
      setOpen(false);
      toast.success("Incident status updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update incident status");
    },
  });

  function resetForm() {
    setStatus(
      incident.status === "RESOLVED" ? "FALSE_POSITIVE" : "RESOLVED",
    );
    setReason("");
    setNotes("");
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button type="button">Resolve</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resolve Incident</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`incident-status-${incident.id}`}>Status</Label>
            <Select
              value={status}
              onValueChange={(value: IncidentStatus) => setStatus(value)}
            >
              <SelectTrigger
                id={`incident-status-${incident.id}`}
                className="w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INCIDENT_STATUS.map((option) => (
                  <SelectItem
                    key={option}
                    value={option}
                    disabled={option === incident.status}
                  >
                    {STATUS_LABELS[option]}
                    {option === incident.status ? " (current)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`incident-reason-${incident.id}`}>Reason</Label>
            <Input
              id={`incident-reason-${incident.id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Reason for this status change"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`incident-notes-${incident.id}`}>Notes</Label>
            <Textarea
              id={`incident-notes-${incident.id}`}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Additional notes"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={status === incident.status || mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  status,
                  reason: reason.trim() || undefined,
                  notes: notes.trim() || undefined,
                })
              }
            >
              {mutation.isPending ? "Updating..." : "Update status"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignUserPicker({
  value,
  onValueChange,
}: {
  value: string | null;
  onValueChange: (userId: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [rawSearch, setSearch] = React.useState("");
  const [search] = useDebouncedValue(rawSearch, { wait: 200 });

  const users = useQuery({
    ...usersListQueryOptions({
      page: 1,
      per_page: 50,
      search,
      is_active: true,
    }),
    placeholderData: keepPreviousData,
  });

  const selected = users.data?.items.find((user) => user.id === value);

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selected?.full_name ?? "Select a user..."}
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command className="max-h-80">
          <CommandInput
            placeholder="Search users..."
            value={rawSearch}
            onValueChange={setSearch}
          />
          {users.isSuccess ? (
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {users.data.items.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={user.id}
                    keywords={[user.full_name, user.email]}
                    onSelect={() => {
                      onValueChange(user.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span>{user.full_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {user.email}
                      </span>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-auto size-4",
                        value === user.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          ) : (
            <Spinner className="m-4 self-center" />
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
